import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { toast } from 'react-toastify';
import { externalReviewAPI, currentTenantId } from '../../services/api';
import {
  FiFileText, FiSend, FiLock, FiCheckCircle, FiAlertCircle,
  FiClock, FiDownload, FiMessageSquare, FiEye, FiX, FiRefreshCw,
  FiShield, FiChevronDown, FiChevronUp, FiArrowLeft, FiEdit3, FiSave
} from 'react-icons/fi';
import { formatDateTime } from '../../utils/dateFormat';
import './ExternalReview.scss';

const ONLYOFFICE_URL = process.env.REACT_APP_ONLYOFFICE_URL || 'http://localhost:8080';

function ExternalReview() {
  const { accessToken } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State machine: 'loading' -> 'otp' -> 'review' -> 'completed' | 'expired' | 'error'
  const [phase, setPhase] = useState('loading');
  const [reviewInfo, setReviewInfo] = useState(null);
  const [error, setError] = useState('');

  // OTP state
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpResending, setOtpResending] = useState(false);
  const [otpResent, setOtpResent] = useState(false);
  const otpRefs = useRef([]);

  // Review state
  const [documents, setDocuments] = useState([]);
  const [contractId, setContractId] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showConfirmComplete, setShowConfirmComplete] = useState(false);
  const [sessionTimer, setSessionTimer] = useState(null);
  const [expandedDoc, setExpandedDoc] = useState(null);

  // Editor state
  const [editorDocId, setEditorDocId] = useState(null);
  const [editorDocTitle, setEditorDocTitle] = useState('');
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorError, setEditorError] = useState('');
  const [editorMode, setEditorMode] = useState('view'); // 'view' | 'edit'
  const [editorDocumentKey, setEditorDocumentKey] = useState('');
  const [saving, setSaving] = useState(false);
  const editorInstanceRef = useRef(null);
  const editorHostRef = useRef(null); // portal host node on document.body
  const presenceWsRef = useRef(null); // WS connection for presence while editor is open
  const presenceHeartbeatRef = useRef(null);

  const loadDocuments = useCallback(async () => {
    try {
      const { data } = await externalReviewAPI.getDocuments(accessToken);
      setDocuments(data.documents || []);
      setContractId(data.contractId);
      if (Array.isArray(data.comments)) setComments(data.comments);
    } catch (err) {
      if (err.response?.data?.sessionExpired) {
        setPhase('otp');
        setOtpError(t('externalReview.sessionExpired'));
      }
    }
  }, [accessToken]);

  // Verify access token on mount
  useEffect(() => {
    async function verify() {
      try {
        const { data } = await externalReviewAPI.verifyAccess(accessToken);
        setReviewInfo(data);

        if (data.status === 'completed') {
          setPhase('completed');
        } else if (data.needsOTP) {
          setPhase('otp');
        } else {
          setPhase('review');
          loadDocuments();
        }
      } catch (err) {
        const status = err.response?.status;
        if (status === 410) {
          setPhase('expired');
          setError(err.response?.data?.error || t('externalReview.accessExpiredDesc'));
        } else if (status === 404) {
          setPhase('error');
          setError(t('externalReview.invalidAccessLink'));
        } else {
          setPhase('error');
          setError(t('externalReview.errorDesc'));
        }
      }
    }
    verify();
  }, [accessToken, loadDocuments]);

  // OTP input handling
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...otpDigits];
    newDigits[index] = value.slice(-1);
    setOtpDigits(newDigits);
    setOtpError('');

    // Auto-advance
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits filled
    const code = newDigits.join('');
    if (code.length === 6) {
      handleVerifyOTP(code);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) {
      const digits = text.split('');
      setOtpDigits(digits);
      otpRefs.current[5]?.focus();
      handleVerifyOTP(text);
    }
  };

  const handleVerifyOTP = async (code) => {
    if (!code || code.length !== 6) return;
    setOtpVerifying(true);
    setOtpError('');
    try {
      const { data } = await externalReviewAPI.verifyOTP(accessToken, code);
      // Calculate session timer
      const expiresAt = new Date(data.sessionExpiresAt);
      setSessionTimer(expiresAt);
      setPhase('review');
      loadDocuments();
    } catch (err) {
      setOtpError(err.response?.data?.error || 'Invalid code. Please try again.');
      setOtpDigits(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    setOtpResending(true);
    setOtpResent(false);
    try {
      await externalReviewAPI.resendOTP(accessToken);
      setOtpResent(true);
      setOtpError('');
      setOtpDigits(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
      setTimeout(() => setOtpResent(false), 5000);
    } catch (err) {
      setOtpError(err.response?.data?.error || t('externalReview.failedToResend'));
    } finally {
      setOtpResending(false);
    }
  };

  // Comments
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSubmittingComment(true);
    try {
      const { data } = await externalReviewAPI.addComment(accessToken, {
        documentId: selectedDocId,
        content: newComment.trim(),
      });
      setComments([...comments, data.comment]);
      setNewComment('');
    } catch (err) {
      if (err.response?.data?.sessionExpired) {
        setPhase('otp');
        setOtpError(t('externalReview.sessionExpired'));
      }
    } finally {
      setSubmittingComment(false);
    }
  };

  // Download document
  const handleDownload = (docId, docTitle) => {
    const url = externalReviewAPI.downloadDocumentUrl(accessToken, docId);
    fetch(url, { headers: { 'x-tenant-id': currentTenantId } })
      .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `${docTitle}.docx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => setError(t('externalReview.failedToDownload')));
  };

  // Open document in ONLYOFFICE editor (view + comment mode)
  const handleOpenEditor = async (docId, docTitle) => {
    setEditorDocId(docId);
    setEditorDocTitle(docTitle);
    setEditorLoading(true);
    setEditorError('');
  };

  // Create / destroy the portal host node on document.body (fully outside React)
  useEffect(() => {
    if (editorDocId) {
      // Create a host div on body for the ONLYOFFICE iframe
      const host = document.createElement('div');
      host.id = 'external-editor-portal';
      host.className = 'er-editor-portal er-editor-portal--visible';
      document.body.appendChild(host);
      editorHostRef.current = host;

      // Create the target div ONLYOFFICE will take over (pure DOM, not React)
      const editorDiv = document.createElement('div');
      editorDiv.id = 'external-onlyoffice-editor';
      editorDiv.style.cssText = 'width:100%;height:100%';
      host.appendChild(editorDiv);
    }
    return () => {
      // Tear down: destroy editor + remove host from body
      if (editorInstanceRef.current) {
        try { editorInstanceRef.current.destroyEditor(); } catch (_) { /* ignore */ }
        editorInstanceRef.current = null;
      }
      if (editorHostRef.current) {
        editorHostRef.current.remove();
        editorHostRef.current = null;
      }
    };
  }, [editorDocId]);

  // Initialize ONLYOFFICE when editorDocId is set
  useEffect(() => {
    if (!editorDocId || !editorLoading) return;

    let cancelled = false;

    (async () => {
      try {
        const { data } = await externalReviewAPI.getEditorSession(accessToken, editorDocId);
        if (cancelled) return;

        const { editorConfig, editMode } = data;
        setEditorMode(editMode || editorConfig?.editorConfig?.mode || 'view');
        setEditorDocumentKey(editorConfig?.document?.key || '');
        setEditorLoading(false);

        // Wait a frame for the portal host to be in the DOM
        requestAnimationFrame(() => {
          const scriptId = 'onlyoffice-api-script';
          let script = document.getElementById(scriptId);

          const initEditor = () => {
            const target = document.getElementById('external-onlyoffice-editor');
            if (window.DocsAPI && target) {
              try {
                editorInstanceRef.current = new window.DocsAPI.DocEditor('external-onlyoffice-editor', editorConfig);
              } catch (err) {
                console.error('ONLYOFFICE init error:', err);
                setEditorError(t('externalReview.failedToLoadEditor'));
              }
            }
          };

          if (!script) {
            script = document.createElement('script');
            script.id = scriptId;
            script.src = `${ONLYOFFICE_URL}/web-apps/apps/api/documents/api.js`;
            script.async = true;
            script.onload = initEditor;
            script.onerror = () => setEditorError(t('externalReview.failedToLoadEditorService'));
            document.head.appendChild(script);
          } else {
            initEditor();
          }
        });
      } catch (err) {
        if (!cancelled) {
          setEditorLoading(false);
          if (err.response?.data?.sessionExpired) {
            setPhase('otp');
            setOtpError(t('externalReview.sessionExpired'));
          } else {
            setEditorError(err.response?.data?.error || t('externalReview.failedToLoadEditorSession'));
          }
        }
      }
    })();

    return () => { cancelled = true; };
  }, [editorDocId, editorLoading, accessToken]);

  // Presence WebSocket — while the editor is open, connect to the internal
  // /ws endpoint so the external reviewer shows up in the document's presence
  // list alongside internal users.
  //
  // SEC-27 / M1: the access token no longer rides in the URL. The socket opens
  // bare, the first frame is { event: 'auth', data: { externalAccessToken,
  // tenantId } }, and join/heartbeat wait for { event: 'auth.ok' }. A 4001
  // close means the token was rejected — nothing to retry here.
  useEffect(() => {
    if (!editorDocId || !reviewInfo) return;

    const wsBase = process.env.REACT_APP_WS_URL || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;

    let ws;
    try {
      ws = new WebSocket(wsBase);
    } catch (err) {
      console.error('[EXT-WS] connect error:', err);
      return;
    }
    presenceWsRef.current = ws;
    let authed = false;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        event: 'auth',
        data: { externalAccessToken: accessToken, tenantId: currentTenantId },
      }));
    };

    ws.onmessage = (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch { return; }
      if (msg?.event !== 'auth.ok' || authed) return;
      authed = true;
      ws.send(JSON.stringify({
        event: 'document.presence.join',
        data: { documentId: editorDocId },
      }));
      presenceHeartbeatRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            event: 'document.presence.heartbeat',
            data: { documentId: editorDocId },
          }));
        }
      }, 30000);
    };

    ws.onerror = () => { /* onclose handles cleanup */ };
    ws.onclose = () => {
      authed = false;
      if (presenceHeartbeatRef.current) clearInterval(presenceHeartbeatRef.current);
      presenceHeartbeatRef.current = null;
    };

    return () => {
      if (presenceHeartbeatRef.current) clearInterval(presenceHeartbeatRef.current);
      presenceHeartbeatRef.current = null;
      if (ws && authed && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({
            event: 'document.presence.leave',
            data: { documentId: editorDocId },
          }));
        } catch (_) { /* ignore */ }
        ws.close(1000, 'Editor closed');
      } else if (ws) {
        try { ws.close(); } catch (_) { /* ignore */ }
      }
      presenceWsRef.current = null;
    };
  }, [editorDocId, accessToken, reviewInfo]);

  // Close editor
  const handleCloseEditor = () => {
    setEditorDocId(null);
    setEditorDocTitle('');
    setEditorError('');
    setEditorMode('view');
    setEditorDocumentKey('');
    // Cleanup happens in the editorDocId useEffect's return
  };

  // Explicit save — triggers OnlyOffice forcesave. The backend returns either
  // ok:true (save accepted) or ok:true + noChanges:true (nothing to persist).
  const handleSave = async () => {
    if (!editorDocId || saving) return;
    setSaving(true);
    try {
      const res = await externalReviewAPI.save(accessToken, editorDocId, editorDocumentKey);
      if (res.data?.noChanges) {
        toast.info(t('externalReview.noChangesToSave'));
      } else {
        toast.success(t('externalReview.saveChangesSuccess'));
      }
    } catch (err) {
      if (err.response?.data?.sessionExpired) {
        setPhase('otp');
        setOtpError(t('externalReview.sessionExpired'));
      } else {
        toast.error(err.response?.data?.error || t('externalReview.saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Complete review
  const handleCompleteReview = async () => {
    setCompleting(true);
    try {
      await externalReviewAPI.completeReview(accessToken);
      navigate(`/external-review/${accessToken}/thank-you`);
    } catch (err) {
      const msg = err.response?.data?.error || '';
      // If already completed, just navigate to thank-you
      if (msg.toLowerCase().includes('already been completed')) {
        navigate(`/external-review/${accessToken}/thank-you`);
      } else {
        setShowConfirmComplete(false);
        setError(msg || t('externalReview.failedToCompleteReview'));
      }
    } finally {
      setCompleting(false);
    }
  };

  // Session countdown
  const SessionCountdown = () => {
    const [remaining, setRemaining] = useState('');

    useEffect(() => {
      if (!sessionTimer) return;
      const tick = () => {
        const diff = sessionTimer - new Date();
        if (diff <= 0) {
          setPhase('otp');
          setOtpError(t('externalReview.sessionExpired'));
          return;
        }
        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        setRemaining(`${mins}:${secs.toString().padStart(2, '0')}`);
      };
      tick();
      const interval = setInterval(tick, 1000);
      return () => clearInterval(interval);
    }, []);

    if (!remaining) return null;
    return (
      <div className="er__session-timer">
        <FiClock size={13} />
        <span>{t('externalReview.session')}: {remaining}</span>
      </div>
    );
  };

  // ─── LOADING ───
  if (phase === 'loading') {
    return (
      <div className="er">
        <div className="er__center">
          <div className="er__loading-card">
            <div className="er__spinner" />
            <p>{t('externalReview.verifyingAccess')}</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── EXPIRED ───
  if (phase === 'expired') {
    return (
      <div className="er">
        <div className="er__center">
          <div className="er__status-card er__status-card--expired">
            <div className="er__status-icon er__status-icon--expired">
              <FiClock size={32} />
            </div>
            <h1>{t('externalReview.accessExpired')}</h1>
            <p>{error || t('externalReview.accessExpiredDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── ERROR ───
  if (phase === 'error') {
    return (
      <div className="er">
        <div className="er__center">
          <div className="er__status-card er__status-card--error">
            <div className="er__status-icon er__status-icon--error">
              <FiAlertCircle size={32} />
            </div>
            <h1>{t('externalReview.somethingWentWrong')}</h1>
            <p>{error || t('externalReview.errorDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── COMPLETED (already) ───
  if (phase === 'completed') {
    return (
      <div className="er">
        <div className="er__center">
          <div className="er__status-card er__status-card--completed">
            <div className="er__status-icon er__status-icon--completed">
              <FiCheckCircle size={32} />
            </div>
            <h1>{t('externalReview.reviewCompleted')}</h1>
            <p>{t('externalReview.reviewCompletedDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── OTP VERIFICATION ───
  if (phase === 'otp') {
    return (
      <div className="er">
        <div className="er__center">
          <div className="er__auth">
            <div className="er__steps">
              <div className="er__step er__step--active">
                <span className="er__step-dot">1</span>
                <span className="er__step-label">{t('externalReview.stepVerify')}</span>
              </div>
              <div className="er__step-line" />
              <div className="er__step">
                <span className="er__step-dot">2</span>
                <span className="er__step-label">{t('externalReview.stepReview')}</span>
              </div>
              <div className="er__step-line" />
              <div className="er__step">
                <span className="er__step-dot">3</span>
                <span className="er__step-label">{t('externalReview.stepSubmit')}</span>
              </div>
            </div>

            <div className="er__otp-card">
              <div className="er__otp-shield">
                <FiShield size={26} />
              </div>
              <div className="er__secure-badge">
                <FiLock size={11} />
                {t('externalReview.securePortal')}
              </div>
              <h1>{t('externalReview.verificationRequired')}</h1>
              <p className="er__otp-subtitle">
                {t('externalReview.otpSubtitle')}
              </p>

            {reviewInfo && (
              <div className="er__otp-contract-info">
                <FiFileText size={14} />
                <span>{t('externalReview.contract')} <strong>{reviewInfo.contractNumber}</strong></span>
              </div>
            )}

            <div className="er__otp-inputs" onPaste={handleOtpPaste}>
              {otpDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={el => otpRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  className={`er__otp-input ${otpError ? 'er__otp-input--error' : ''} ${digit ? 'er__otp-input--filled' : ''}`}
                  autoFocus={i === 0}
                  disabled={otpVerifying}
                />
              ))}
            </div>

            {otpError && (
              <div className="er__otp-error">
                <FiAlertCircle size={14} />
                {otpError}
              </div>
            )}

            {otpVerifying && (
              <div className="er__otp-verifying">
                <div className="er__spinner er__spinner--sm" />
                {t('externalReview.verifying')}
              </div>
            )}

            {otpResent && (
              <div className="er__otp-success">
                <FiCheckCircle size={14} />
                {t('externalReview.newCodeSent')}
              </div>
            )}

            <div className="er__otp-actions">
              <button
                className="er__otp-resend"
                onClick={handleResendOTP}
                disabled={otpResending}
              >
                <FiRefreshCw size={14} className={otpResending ? 'er__spin' : ''} />
                {otpResending ? t('externalReview.sending') : t('externalReview.resendCode')}
              </button>
            </div>

              <p className="er__otp-hint">
                <FiLock size={12} />
                {t('externalReview.otpHint')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── REVIEW PHASE ───
  // Editor header/loading/error rendered via portal into a body-level node.
  // The ONLYOFFICE div (#external-onlyoffice-editor) is created via pure DOM
  // in the useEffect above — React never sees or manages it.

  const editorPortalUI = editorDocId && editorHostRef.current && createPortal(
    <>
      <header className="er__header" style={{ flexShrink: 0 }}>
        <div className="er__header-left">
          <button className="er__back-btn" onClick={handleCloseEditor}>
            <FiArrowLeft size={16} />
            <span>{t('externalReview.backToDocuments')}</span>
          </button>
          <div className="er__header-divider" />
          <div className="er__header-info">
            <h1>{editorDocTitle || t('externalReview.document')}</h1>
            <span className="er__header-contract">
              {editorMode === 'edit' ? t('externalReview.editMode') : t('externalReview.viewCommentMode')}
            </span>
          </div>
        </div>
        <div className="er__header-right">
          <SessionCountdown />
          {editorMode === 'edit' && !editorLoading && !editorError && (
            <button
              className="er__save-btn"
              onClick={handleSave}
              disabled={saving}
              title={t('externalReview.save')}
            >
              {saving ? <div className="er__spinner er__spinner--sm" /> : <FiSave size={14} />}
              <span>{saving ? t('externalReview.saving') : t('externalReview.save')}</span>
            </button>
          )}
          <div className="er__reviewer-badge">
            {editorMode === 'edit' ? <FiEdit3 size={13} /> : <FiEye size={13} />}
            <span>{editorMode === 'edit' ? t('externalReview.editing') : t('externalReview.viewOnly')}</span>
          </div>
          {reviewInfo && (
            <div className="er__reviewer-chip">
              <span className="er__reviewer-avatar">
                {(reviewInfo.externalPartyName || '?').split(' ').map(w => w[0]).slice(0, 2).join('')}
              </span>
              <span className="er__reviewer-chip-name">{reviewInfo.externalPartyName}</span>
            </div>
          )}
        </div>
      </header>
      {editorLoading && (
        <div className="er__editor-loading">
          <div className="er__spinner" />
          <p>{t('externalReview.loadingDocumentEditor')}</p>
        </div>
      )}
      {editorError && (
        <div className="er__editor-error">
          <FiAlertCircle size={24} />
          <p>{editorError}</p>
          <button className="er__back-btn" onClick={handleCloseEditor}>
            <FiArrowLeft size={14} /> {t('externalReview.backToDocuments')}
          </button>
        </div>
      )}
    </>,
    editorHostRef.current
  );

  return (
    <>
    {editorPortalUI}

    {/* ─── REVIEW VIEW ─── */}
    <div className="er" style={{ display: editorDocId ? 'none' : undefined }}>
      {/* Header */}
      <header className="er__header">
        <div className="er__header-left">
          <div className="er__header-logo">
            <FiFileText size={20} />
          </div>
          <div className="er__header-info">
            <h1>{t('externalReview.contractReview')}</h1>
            {reviewInfo && (
              <span className="er__header-contract">{reviewInfo.contractNumber}</span>
            )}
          </div>
        </div>
        <div className="er__header-right">
          <SessionCountdown />
          <div className="er__reviewer-badge">
            <FiEye size={13} />
            <span>{t('externalReview.viewOnly')}</span>
          </div>
          {reviewInfo && (
            <div className="er__reviewer-chip">
              <span className="er__reviewer-avatar">
                {(reviewInfo.externalPartyName || '?').split(' ').map(w => w[0]).slice(0, 2).join('')}
              </span>
              <span className="er__reviewer-chip-name">{reviewInfo.externalPartyName}</span>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="er__body">
        {/* Welcome strip */}
        <div className="er__welcome">
          <div className="er__welcome-text">
            <h2>{t('externalReview.welcome', { name: (reviewInfo?.externalPartyName || '').split(' ')[0] })}</h2>
            <p>{t('externalReview.welcomeDesc', { contract: reviewInfo?.contractNumber || '' })}</p>
          </div>
          <div className="er__welcome-meta">
            <div className="er__welcome-stat">
              <FiFileText size={14} />
              <span><strong>{documents.length}</strong> {t('externalReview.documents')}</span>
            </div>
            <div className="er__welcome-stat">
              <FiMessageSquare size={14} />
              <span><strong>{comments.length}</strong> {t('externalReview.yourComments')}</span>
            </div>
          </div>
        </div>

        <div className="er__content">
        {/* Documents Panel */}
        <div className="er__panel er__panel--docs">
          <div className="er__panel-header">
            <FiFileText size={16} />
            <h2>{t('externalReview.documents')}</h2>
            <span className="er__panel-count">{documents.length}</span>
          </div>
          <div className="er__docs-list">
            {documents.length === 0 ? (
              <div className="er__empty">
                <FiFileText size={24} />
                <p>{t('externalReview.noDocumentsAvailable')}</p>
              </div>
            ) : (
              documents.map((doc) => (
                <div
                  key={doc._id}
                  className={`er__doc-item ${expandedDoc === doc._id ? 'er__doc-item--expanded' : ''}`}
                >
                  <div className="er__doc-main" onClick={() => setExpandedDoc(expandedDoc === doc._id ? null : doc._id)}>
                    <div className="er__doc-icon">
                      <FiFileText size={18} />
                    </div>
                    <div className="er__doc-info">
                      <h3>{doc.title}</h3>
                      {doc.snapshotVersion && (
                        <span className="er__doc-version">
                          {t('externalReview.version')} {doc.snapshotVersion.versionNumber}
                        </span>
                      )}
                    </div>
                    <button className="er__doc-toggle">
                      {expandedDoc === doc._id ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                    </button>
                  </div>
                  {expandedDoc === doc._id && (
                    <div className="er__doc-actions">
                      <button
                        className="er__doc-action-btn"
                        onClick={() => handleDownload(doc._id, doc.title)}
                      >
                        <FiDownload size={14} />
                        {t('externalReview.download')}
                      </button>
                      <button
                        className="er__doc-action-btn er__doc-action-btn--primary"
                        onClick={() => handleOpenEditor(doc._id, doc.title)}
                      >
                        <FiEdit3 size={14} />
                        {t('externalReview.openComment')}
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Comments Panel */}
        <div className="er__panel er__panel--comments">
          <div className="er__panel-header">
            <FiMessageSquare size={16} />
            <h2>{t('externalReview.yourComments')}</h2>
            <span className="er__panel-count">{comments.length}</span>
          </div>

          <div className="er__comments-list">
            {comments.length === 0 ? (
              <div className="er__empty">
                <FiMessageSquare size={24} />
                <p>{t('externalReview.noCommentsYet')}</p>
              </div>
            ) : (
              comments.map((comment, i) => {
                const docName = documents.find(d => d._id === comment.documentId)?.title;
                return (
                  <div key={i} className="er__comment">
                    {docName && (
                      <div className="er__comment-doc">
                        <FiFileText size={11} />
                        {docName}
                      </div>
                    )}
                    <p className="er__comment-text">{comment.content}</p>
                    <span className="er__comment-time">
                      {formatDateTime(comment.createdAt)}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Comment Input */}
          <div className="er__comment-input-area">
            {selectedDocId && (
              <div className="er__comment-doc-badge">
                <FiFileText size={12} />
                <span>{documents.find(d => d._id === selectedDocId)?.title}</span>
                <button onClick={() => setSelectedDocId(null)}><FiX size={12} /></button>
              </div>
            )}
            <div className="er__comment-input-row">
              <textarea
                className="er__comment-textarea"
                placeholder={t('externalReview.writeComment')}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={2}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddComment();
                  }
                }}
              />
              <button
                className="er__comment-send"
                onClick={handleAddComment}
                disabled={submittingComment || !newComment.trim()}
              >
                {submittingComment ? <div className="er__spinner er__spinner--sm" /> : <FiSend size={16} />}
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="er__bottom-bar">
        <div className="er__bottom-info">
          <FiAlertCircle size={14} />
          <span>{t('externalReview.loseAccessWarning')}</span>
        </div>
        <button
          className="er__complete-btn"
          onClick={() => setShowConfirmComplete(true)}
          disabled={completing}
        >
          <FiCheckCircle size={16} />
          {t('externalReview.reviewDone')}
        </button>
      </div>

      {/* Confirm Complete Modal */}
      {showConfirmComplete && (
        <div className="er__modal-overlay" onClick={() => setShowConfirmComplete(false)}>
          <div className="er__modal" onClick={(e) => e.stopPropagation()}>
            <div className="er__modal-icon">
              <FiCheckCircle size={32} />
            </div>
            <h2>{t('externalReview.completeReviewTitle')}</h2>
            {/* SEC-10: <Trans> renders the <strong> markup from the locale string
                as a component instead of injecting HTML (i18n escapeValue is off). */}
            <p>
              <Trans i18nKey="externalReview.completeReviewDesc" components={{ strong: <strong /> }} />
            </p>
            {comments.length > 0 && (
              <div className="er__modal-summary">
                <FiMessageSquare size={14} />
                <span>
                  <Trans
                    i18nKey="externalReview.commentsLeft"
                    values={{ count: comments.length }}
                    components={{ strong: <strong /> }}
                  />
                </span>
              </div>
            )}
            <div className="er__modal-actions">
              <button
                className="er__modal-btn er__modal-btn--outline"
                onClick={() => setShowConfirmComplete(false)}
              >
                {t('externalReview.continueReviewing')}
              </button>
              <button
                className="er__modal-btn er__modal-btn--primary"
                onClick={handleCompleteReview}
                disabled={completing}
              >
                {completing ? (
                  <><div className="er__spinner er__spinner--sm" /> {t('externalReview.submitting')}</>
                ) : (
                  <><FiCheckCircle size={14} /> {t('externalReview.yesCompleteReview')}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export default ExternalReview;
